import { useCallback, useState } from 'react';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import * as z from 'zod';

/**
 * The home screen's drawer URL state: `?dialog=favorites|downloads` on `/`,
 * shared by every template's home so the contract lives in one place.
 *
 * `dialog` mirrors which of the home screen's two drawers (Favorites/
 * Downloads) is open. The home keeps it in sync with each drawer's own
 * open/close state via a `replace` navigation, not a `push` -- so opening
 * a drawer never adds its own back-button stop, but the URL the reader is
 * already on (home) carries which drawer was open when they left it. A
 * favorite/downloaded-issue link is a normal `push`, so pressing Back from
 * the article/issue they opened lands back on that same URL and the
 * drawer reopens instead of the reader returning to a bare home screen.
 *
 * Unlike `settings-route.ts`'s param, this one is declared: each template's
 * `/` route adds `dialog: homeDialogSchema` to its own `validateSearch`
 * schema (templates differ in the rest of their home search).
 */
export const homeDialogSchema = z.enum(['favorites', 'downloads']).optional();

export type HomeDialog = z.infer<typeof homeDialogSchema>;

interface HomeDialogSearch {
	dialog?: HomeDialog;
}

const routeApi = getRouteApi('/');

/**
 * The Favorites/Downloads drawers' open state, seeded from and mirrored
 * into `?dialog=`. Seeded from the URL so a direct/restored link opens
 * straight into the matching drawer -- most importantly, landing back on
 * home via the browser's Back button after following a favorite/
 * downloaded-issue link out of an already-open drawer (the drawers' own
 * item click deliberately leaves this entry's URL alone rather than
 * clearing it before navigating away).
 */
export function useHomeDrawers() {
	const navigate = useNavigate();
	const search: HomeDialogSearch = routeApi.useSearch();
	const [isFavoritesOpen, setIsFavoritesOpenState] = useState(
		() => search.dialog === 'favorites',
	);
	const [isDownloadsOpen, setIsDownloadsOpenState] = useState(
		() => search.dialog === 'downloads',
	);

	const setDialogSearch = useCallback(
		(dialog: HomeDialog) => {
			void navigate({
				to: '/',
				// Keeps the rest of the template's own home search.
				search: (prev: HomeDialogSearch) => ({ ...prev, dialog }),
				replace: true,
				// Explicit, not just the omitted-prop default: this fires
				// while the reader is navigating *back* to a dialog-bearing
				// home as well as on a plain open/close, and a cover-morph
				// view transition has no business replaying itself for
				// either -- it's restoring state the reader already saw, not
				// a fresh navigation into new content.
				viewTransition: false,
			});
		},
		[navigate],
	);
	const setIsFavoritesOpen = useCallback(
		(value: boolean) => {
			setIsFavoritesOpenState(value);
			setDialogSearch(value ? 'favorites' : undefined);
		},
		[setDialogSearch],
	);
	const setIsDownloadsOpen = useCallback(
		(value: boolean) => {
			setIsDownloadsOpenState(value);
			setDialogSearch(value ? 'downloads' : undefined);
		},
		[setDialogSearch],
	);

	return {
		isFavoritesOpen,
		setIsFavoritesOpen,
		isDownloadsOpen,
		setIsDownloadsOpen,
	};
}
